import { useEffect, useRef, useState } from 'react';

/** Delay before the sources control appears when the bar activates from a chip. */
const CHIP_HANDOFF_REVEAL_MS = 200;

/**
 * Owns the reveal timing of the active-state sources control.
 *
 * Activating the bar from an inactive chip animates the chip out first, so the
 * sources control is held back briefly to avoid the two overlapping. Every other
 * activation reveals it immediately, and deactivating hides it at once.
 *
 * Takes whether there is a chip rather than the chip itself. The caller's chip
 * is a memoised object literal, so its identity changes whenever any of its own
 * inputs do. As an effect dependency that re-runs this scheduler on churn that
 * does not change the answer, and a re-run inside the handoff window clears the
 * pending timer and reveals immediately, skipping the delay it exists for. A
 * boolean can only change when the answer changes.
 */
export const useActiveSourcesReveal = (
  isBarActive: boolean,
  hasInactiveBarChip: boolean
): boolean => {
  const prevIsBarActiveRef = useRef(isBarActive);
  const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showActiveSources, setShowActiveSources] = useState(isBarActive);

  useEffect(() => {
    const wasBarActive = prevIsBarActiveRef.current;
    const isActivatingBar = isBarActive && !wasBarActive;

    if (revealTimeoutRef.current) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }

    if (!isBarActive) {
      setShowActiveSources(false);
    } else if (isActivatingBar && hasInactiveBarChip) {
      setShowActiveSources(false);
      revealTimeoutRef.current = setTimeout(() => {
        setShowActiveSources(true);
        revealTimeoutRef.current = null;
      }, CHIP_HANDOFF_REVEAL_MS);
    } else {
      setShowActiveSources(true);
    }

    prevIsBarActiveRef.current = isBarActive;
  }, [hasInactiveBarChip, isBarActive]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        clearTimeout(revealTimeoutRef.current);
      }
    },
    []
  );

  return showActiveSources;
};
